import React, { useEffect, useState } from 'react';
import PageHeader from '@/components/PageHeader';
import AdminBillingNav from '@/components/adminBilling/AdminBillingNav';
import StatCard from '@/components/adminBilling/StatCard';
import SimpleBarChart from '@/components/adminBilling/SimpleBarChart';
import StatusChip from '@/components/adminBilling/StatusChip';
import { adminBillingService, formatCents, type AdminBillingDashboard } from '@/api/services/adminBilling';
import { formatApiError } from '@/utils/formatApiError';
import { DataTableScrollArea } from '@/components/table';

export default function AdminBillingDashboardPage() {
  const [data, setData] = useState<AdminBillingDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = (refresh = false) => {
    setLoading(true);
    adminBillingService
      .dashboard(refresh)
      .then(setData)
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const cards = data?.cards ?? {};

  return (
    <div className="admin-billing-page">
      <AdminBillingNav />
      <PageHeader
        title="Billing dashboard"
        subtitle="Operational overview for running Scoutly as a SaaS."
      >
        <button type="button" className="btn btn-secondary" onClick={() => load(true)}>
          Refresh
        </button>
      </PageHeader>
      {error ? <div className="alert alert-error">{error}</div> : null}
      {loading && !data ? <div className="panel">Loading dashboard…</div> : null}
      {data ? (
        <>
          <div className="admin-billing-stat-grid">
            <StatCard label="MRR" value={formatCents(Number(cards.mrrCents))} />
            <StatCard label="ARR" value={formatCents(Number(cards.arrCents))} />
            <StatCard label="Active subscriptions" value={cards.activeSubscriptions ?? 0} />
            <StatCard label="Trial orgs" value={cards.trialOrganizations ?? 0} />
            <StatCard label="Grace period" value={cards.gracePeriod ?? 0} />
            <StatCard label="Expiring in 7 days" value={cards.expiringIn7Days ?? 0} />
            <StatCard label="Expired" value={cards.expired ?? 0} />
            <StatCard label="Payment failures (30d)" value={cards.paymentFailures ?? 0} />
            <StatCard label="Manual grants" value={cards.manualGrants ?? 0} />
            <StatCard label="Stripe subscriptions" value={cards.stripeSubscriptions ?? 0} />
            <StatCard
              label="Avg revenue / org"
              value={formatCents(Number(cards.averageRevenuePerOrgCents))}
            />
            <StatCard label="Conversion rate" value={`${cards.conversionRatePercent ?? 0}%`} />
            <StatCard
              label="Top plan"
              value={String(cards.topPlanName || cards.topPlanSlug || '—')}
            />
            <StatCard label="New orgs this month" value={cards.newOrganizationsThisMonth ?? 0} />
            <StatCard
              label="Payment success rate"
              value={`${cards.paymentSuccessRatePercent ?? 100}%`}
            />
          </div>
          <div className="admin-billing-charts-row">
            <SimpleBarChart
              title="Subscriptions by plan"
              items={data.charts.subscriptionsByPlan as Record<string, unknown>[]}
              labelKey="planName"
            />
            <SimpleBarChart
              title="Subscription lifecycle"
              items={data.charts.subscriptionLifecycle as Record<string, unknown>[]}
              labelKey="status"
            />
          </div>
          <section className="panel">
            <h2>Recent payments</h2>
            {!data.recentPayments.length ? (
              <p className="muted">No payments recorded yet.</p>
            ) : (
              <DataTableScrollArea>
        <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Organization</th>
                    <th>Amount</th>
                    <th>Provider</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentPayments.map((p) => (
                    <tr key={String(p.id)}>
                      <td>{p.createdAt ? new Date(String(p.createdAt)).toLocaleString() : '—'}</td>
                      <td>{String(p.organizationName || p.organizationSlug || p.organizationId)}</td>
                      <td>{formatCents(Number(p.amountCents), String(p.currency || 'EUR'))}</td>
                      <td>{String(p.provider)}</td>
                      <td><StatusChip status={String(p.status)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
        </DataTableScrollArea>
            )}
          </section>
          {data.cachedAt ? (
            <p className="admin-billing-cache-note">Cached at {new Date(data.cachedAt).toLocaleTimeString()}</p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
